import { isDesktopRtfCopyEnabled } from '@/features/flags/desktop-rtf-copy';
import { isTauri } from '@/tauri/utils';
import { atom, getDefaultStore } from 'jotai';

type Capability = 'unknown' | 'supported' | 'unavailable';
type RtfCopyState = {
  ownerId: string | null;
  capability: Capability;
  revision: number;
};

const stateAtom = atom<RtfCopyState>({
  ownerId: null,
  capability: 'unknown',
  revision: 0,
});
export const rtfCopyStateAtom = atom((get) => get(stateAtom));

// Explicitly shared with non-React clipboard callers, even inside a nested Jotai Provider.
export const rtfCopyStore = getDefaultStore();

export function setRtfCopyOwner(ownerId: string | null): void {
  const current = rtfCopyStore.get(stateAtom);
  if (current.ownerId === ownerId) return;
  rtfCopyStore.set(stateAtom, {
    ownerId,
    capability: 'unknown',
    revision: current.revision + 1,
  });
}

export function setRtfCopyCapability(ownerId: string, capability: Capability): void {
  const current = rtfCopyStore.get(stateAtom);
  if (current.ownerId !== ownerId || current.capability === capability) return;
  rtfCopyStore.set(stateAtom, {
    ...current,
    capability,
    revision: current.revision + 1,
  });
}

export type DesktopClipboardSnapshot = {
  revision: number;
};

/**
 * Capture at enqueue; any user switch, capability change, desktop-gate change,
 * or release-gate change invalidates the native job.
 *
 * A non-null snapshot selects native routing (flag ON + capability supported).
 * Explicit plain-text copies are routed natively too but never call htmlToRtf.
 * If htmlToRtf throws, the copy degrades to HTML + plain and logs
 * 'rtf_serialization_degraded'; it does NOT degrade silently.
 * The snapshot carries no per-user opt-in preference.
 */
export function getDesktopClipboardSnapshot(): DesktopClipboardSnapshot | null {
  const current = rtfCopyStore.get(stateAtom);
  if (current.ownerId === null || current.capability !== 'supported') return null;
  return isTauri() && isDesktopRtfCopyEnabled() ? { revision: current.revision } : null;
}
